import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import SessionSummarySheet from './SessionSummarySheet';
import SortAssetsSheet from './SortAssetsSheet';
import ExitConfirmSheet from './ExitConfirmSheet';
import ExportTestsSheet from './ExportTestsSheet';
import FinishSessionSheet from './FinishSessionSheet';

/**
 * Every bottom sheet the mobile session can show, and the state that sequences them.
 *
 * Only one overlay is ever open at a time (spec §8). That rule lives in these
 * callbacks: each one closes the sheet it came from before opening the next. Do not
 * collapse the booleans into a single `activeSheet` value without re-checking every
 * transition here — the closes are the rule, not incidental bookkeeping.
 *
 * `summaryOpen` is owned by the caller because the session header's kebab opens it;
 * the other four are internal.
 *
 * Props:
 *   summaryOpen / setSummaryOpen — caller-owned state for the session menu
 *   siteName, totalAssets, remaining, siteEmail — display data
 *   sortBy / setSortBy / sortOrder / setSortOrder — shared sort state
 *   isFinishing — disables the finish action while it runs
 *   onAddAsset — opens the Add Asset form (owned by the caller, not a sheet)
 *   onExportFullReport / onExportDefectReport / onSendEmailReport / onFinish / onCancel — session actions
 */
export default function MobileSessionSheets({
  summaryOpen,
  setSummaryOpen,
  siteName,
  totalAssets,
  remaining,
  siteEmail = '',
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  isFinishing = false,
  onAddAsset,
  onExportFullReport,
  onExportDefectReport,
  onSendEmailReport,
  onFinish,
  onCancel,
  canCancelSession = false,
}) {
  const navigate = useNavigate();
  const [sortSheetOpen, setSortSheetOpen] = useState(false);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);

  return (
    <>
      {/* Session summary sheet */}
      <SessionSummarySheet
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        siteName={siteName}
        totalAssets={totalAssets}
        remaining={remaining}
        onSortAssets={() => { setSummaryOpen(false); setSortSheetOpen(true); }}
        onAddAsset={() => { setSummaryOpen(false); onAddAsset?.(); }}
        onExportTests={() => { setSummaryOpen(false); setExportOpen(true); }}
        onFinishSession={() => {
          setSummaryOpen(false);
          setFinishOpen(true);
        }}
        onBackToDashboard={() => {
          if (remaining > 0) {
            setSummaryOpen(false);
            setExitConfirmOpen(true);
          } else {
            navigate('/');
          }
        }}
        onCancelSession={async () => {
          setSummaryOpen(false);
          try {
            await onCancel?.();
            navigate('/');
          } catch {
            // handled upstream
          }
        }}
        canCancelSession={canCancelSession}
      />

      {/* Sort assets sheet */}
      <SortAssetsSheet
        open={sortSheetOpen}
        onClose={() => setSortSheetOpen(false)}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
      />

      {/* Leave-session confirm (only reachable with remaining > 0) */}
      <ExitConfirmSheet
        open={exitConfirmOpen}
        onKeepTesting={() => setExitConfirmOpen(false)}
        onLeave={() => { setExitConfirmOpen(false); navigate('/'); }}
      />

      {/* Export Tests sheet */}
      <ExportTestsSheet
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        siteEmail={siteEmail}
        onExportFullReport={onExportFullReport}
        onExportDefectReport={onExportDefectReport}
        onSendEmailReport={onSendEmailReport}
      />

      {/* Finish session sheet */}
      <FinishSessionSheet
        open={finishOpen}
        onClose={() => setFinishOpen(false)}
        totalAssets={totalAssets}
        remaining={remaining}
        isFinishing={isFinishing}
        onExportTests={() => { setFinishOpen(false); setExportOpen(true); }}
        onSessionEmail={() => toast('Email export coming — connect mail backend first.', { icon: '📬' })}
        onComplete={async () => {
          try {
            await onFinish?.();
            navigate('/');
          } catch {
            // handled upstream
          }
        }}
      />
    </>
  );
}
